import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { Table, Layout, Button, Card, Upload, message, Modal, Input, Typography, Space, Menu } from 'antd';
import { 
  UploadOutlined, AudioOutlined, FileTextOutlined, SettingOutlined, HistoryOutlined, DollarOutlined, LogoutOutlined, HomeOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';  // 添加这行

const { Header, Content, Footer } = Layout;

function AudioManager() {
  const { t } = useTranslation();
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const navigate = useNavigate();

  const fetchAudioFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/audio/list', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setAudioFiles(data.audioFiles);
      } else {
        message.error('获取音频列表失败');
      }
    } catch (error) {
      console.error('fetch audio list error:', error);
      message.error('获取音频列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudioFiles();
  }, []);

  const handleAudioUpload = async () => {
    if (!audioFile) {
      message.warning(t('audio.selectFileFirst'));
      return;
    }

    const formData = new FormData();
    formData.append('audio', audioFile);

    try {
      const response = await fetch('/api/audio/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('audio.uploadSuccess'));
        setAudioFile(null);
        fetchAudioFiles();
      } else {
        message.error(`${t('audio.uploadFail')}: ${data.message || t('common.unknownError')}`);
      }
    } catch (error) {
      console.error('上传错误:', error);
      message.error(t('audio.uploadError'));
    }
  };

  const handleDeleteAudio = async (id) => {
    Modal.confirm({
      title: t('common.confirmDelete'),
      content: t('audio.confirmDeleteMsg'),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await fetch(`/api/audio/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            message.success(t('common.deleteSuccess'));
            fetchAudioFiles();
          } else {
            message.error(t('common.deleteFail'));
          }
        } catch (error) {
          console.error('删除错误:', error);
          message.error(t('audio.deleteError'));
        }
      },
    });
  };

  const handleAudioRename = async (id, newName) => {
    try {
      const response = await fetch(`/api/audio/${id}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filename: newName }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('audio.renameSuccess'));
        setEditingName(null);
        fetchAudioFiles();
      } else {
        message.error('更新文件名失败');
      }
    } catch (error) {
      console.error('更新文件名错误:', error);
      message.error('更新文件名失败');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('退出失败:', error);
      message.error(t('common.logoutFail'));
    }
  };

  const columns = [
    {
      title: t('audio.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text, record) => (
        editingName === record._id ? (
          <Input
            defaultValue={text}
            onPressEnter={(e) => handleAudioRename(record._id, e.target.value)}
            onBlur={() => setEditingName(null)}
            autoFocus
          />
        ) : (
          <span 
            style={{ cursor: 'pointer' }}
            onClick={() => setEditingName(record._id)}
          >
            {text}
          </span>
        )
      )
    },
    {
      title: t('title.action'),
      key: 'action',
      render: (text, record) => (
        <span>
          <Button onClick={() => navigate(`/editor/${record.subtitleId}`)}>{t('title.dashboard')}</Button>
          <Button onClick={() => handleDeleteAudio(record._id)} style={{ marginLeft: 8 }}>{t('title.delete')}</Button>
        </span>
      ),
    },
  ];

  return (
    <Layout className="layout">
      <Header style={{ background: '#001529', padding: '0 24px', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: '1', display: 'flex', alignItems: 'center' }}>
          <Space align="center">
            <Button 
              icon={<HomeOutlined />} 
              onClick={() => navigate('/dashboard')}
              style={{ 
                background: '#fff',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                height: '32px'
              }}
            >
              {t('title.back')}
            </Button>
            <Typography.Title 
              level={4} 
              style={{ 
                margin: 0,
                color: '#fff',
                lineHeight: '32px'
              }}
            >
              {t('menu.audio')}
            </Typography.Title>
          </Space>
        </div>
        <div style={{marginLeft:8}}><LanguageSelector/></div>
        <Menu
          theme="dark"
          mode="horizontal"
          items={[
            { key: 'audio', label: t('menu.audio'), icon: <AudioOutlined /> },
            { key: 'subtitle', label: t('menu.subtitle'), icon: <FileTextOutlined /> },
            { key: 'rules', label: t('menu.rules'), icon: <SettingOutlined /> },
            { key: 'history', label: t('menu.history'), icon: <HistoryOutlined /> },
            { key: 'plan', label: t('menu.plan'), icon: <DollarOutlined /> },
            { key: 'logout', label: t('menu.logout'), icon: <LogoutOutlined /> }
          ]}
          onClick={({ key }) => {
            // eslint-disable-next-line default-case
            switch(key) {
              case 'audio':
                navigate('/audio-manager');
                break;
              case 'subtitle':
                navigate('/subtitle-manager');
                break;
              case 'rules':
                navigate('/rule-manager');
                break;
              case 'history':
                navigate('/history');
                break;
              case 'plan':
        navigate('/plan');
        break;
              case 'logout':
                handleLogout();
                break;
            }
          }}
          style={{ background: '#001529' }}
        />
      </Header>
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px - 70px)' }}>
        <Card bordered={false}>
          <div>
            <Upload
              beforeUpload={(file) => {
                setAudioFile(file);
                return false;
              }}
              fileList={audioFile ? [audioFile] : []}
              onRemove={() => {
                setAudioFile(null);
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>{t('audio.chooseFile')}</Button>
            </Upload>
            <Button 
              type="primary" 
              onClick={handleAudioUpload} 
              style={{ marginTop: '10px' }}
            >
              {t('audio.uploadFile')}
            </Button>

            <Table
              columns={columns}
              dataSource={audioFiles}
              rowKey="_id"
              loading={loading}
              style={{ marginTop: '20px' }}
            />
          </div>
        </Card>
      </Content>
      <Footer style={{ textAlign: 'center' }}>
        字幕在线编辑器 ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
}

export default AudioManager;